import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatIsoDateToPtBr } from '@/lib/utils';
import type { Amendment, Institution } from '@/services/firestore';
import { Globe, ExternalLink, MapPin, Phone, UserCheck, Link as LinkIcon, AlertCircle, CheckCircle2, XCircle, RotateCcw } from 'lucide-react';
import { AmendmentTimeline, type HistoryItem } from './AmendmentTimeline';
import { getAmendmentHistory, logAmendmentHistory, updateAmendment } from '@/services/firestore';
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { auth } from '@/firebase';

type LinkItem = { label: string; url?: string };

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    amendment: Amendment;
    institution: Institution | null;
    links: LinkItem[];
    onUpdate: () => void;
};

export function ManagerAmendmentModal(props: Props) {
    const { open, onOpenChange, amendment, institution, links, onUpdate } = props;
    const { toast } = useToast();
    const inst = institution;
    const statusLabel = amendment.status || '—';
    const officialCode = amendment.officialCode || '—';
    const formattedDate = formatIsoDateToPtBr(amendment.date);
    const PLACEHOLDER = "data:image/svg+xml;charset=utf-8," + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect fill='#e5e7eb' width='80' height='80'/><text x='40' y='44' text-anchor='middle' font-size='12' fill='#6b7280' font-family='Inter, Arial'>Logo</text></svg>");
    const logoSrc = (inst?.logoUrl && inst.logoUrl.trim().length > 0) ? inst.logoUrl : PLACEHOLDER;

    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [dispatchNote, setDispatchNote] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (open && amendment?.id) {
            setLoadingHistory(true);
            getAmendmentHistory(amendment.id)
                .then(setHistory)
                .catch(() => setHistory([]))
                .finally(() => setLoadingHistory(false));

            setDispatchNote(amendment.dispatchNote || '');
        }
    }, [open, amendment?.id, amendment]);

    const hasDetail = !!(amendment.objectDetail && amendment.objectDetail.trim().length > 0);

    const handleDispatch = async (action: 'aprovada' | 'retificar' | 'reprovada') => {
        if (!amendment.id) return;
        if ((action === 'retificar' || action === 'reprovada') && !dispatchNote.trim()) {
            toast({ title: 'Motivo obrigatório', description: 'Por favor, informe o motivo do despacho.', variant: 'destructive' });
            return;
        }

        setIsProcessing(true);
        try {
            const statusMap = {
                'aprovada': 'Aprovada pelo Gestor',
                'retificar': 'Devolvida para Retificação',
                'reprovada': 'Reprovada pelo Gestor'
            };

            const actionLabels = {
                'aprovada': 'Aprovado pelo Gestor',
                'retificar': 'Devolvida para Retificação',
                'reprovada': 'Reprovada pelo Gestor'
            };

            const changes: any[] = [
                { field: 'status', from: amendment.status, to: statusMap[action] },
                { field: 'dispatchStatus', from: amendment.dispatchStatus, to: action }
            ];

            if (dispatchNote.trim()) {
                changes.push({ field: 'dispatchNote', from: amendment.dispatchNote, to: dispatchNote });
            }

            // Atualizar emenda
            await updateAmendment(amendment.id, {
                status: statusMap[action],
                dispatchStatus: action,
                dispatchNote: dispatchNote.trim() || amendment.dispatchNote,
                detailingSaved: action === 'retificar' ? false : amendment.detailingSaved
            });

            // Registrar no histórico
            const user = auth.currentUser;
            await logAmendmentHistory(amendment.id, {
                actorUid: user?.uid || 'gestor',
                actorEmail: user?.email || 'gestor@sinop.mt.leg.br',
                dateTime: new Date().toISOString(),
                changes: [
                    ...changes,
                    {
                        field: 'action',
                        from: '',
                        to: `${actionLabels[action]}${dispatchNote.trim() ? ': ' + dispatchNote : ''}`
                    }
                ]
            });

            toast({
                title: 'Despacho registrado',
                description: `Emenda ${actionLabels[action].toLowerCase()} com sucesso.`
            });

            // Recarregar histórico
            const updatedHistory = await getAmendmentHistory(amendment.id);
            setHistory(updatedHistory);

            onUpdate();
            onOpenChange(false);
        } catch (error) {
            console.error(error);
            toast({ title: 'Erro ao registrar despacho', description: 'Tente novamente.', variant: 'destructive' });
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="z-[100] p-0 bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto focus:outline-none flex flex-col">
                <DialogHeader className="sr-only">
                    <DialogTitle>Análise de Emenda - Gestor</DialogTitle>
                    <DialogDescription>Visualize e despache a emenda</DialogDescription>
                </DialogHeader>


                <div className="flex flex-col items-center pt-8 pb-6 px-6 border-b border-gray-100 bg-gray-50/50">
                    <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-4 shadow-sm border border-purple-200 overflow-hidden">
                        <img
                            src={logoSrc}
                            alt="Logo"
                            className="h-14 w-14 rounded object-contain"
                            onError={(e) => { const t = e.target as HTMLImageElement; t.src = PLACEHOLDER; }}
                        />
                    </div>
                    <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">Análise de Gestão</span>
                    <h2 className="text-xl font-bold text-gray-900 text-center leading-tight mb-1">{inst?.name || '—'}</h2>
                    <div className="mt-2 flex gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${amendment.dispatchStatus === 'aprovada' ? 'bg-green-100 text-green-700 border-green-200' : amendment.dispatchStatus === 'reprovada' ? 'bg-red-100 text-red-700 border-red-200' : amendment.dispatchStatus === 'retificar' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            Status: {amendment.status || 'Pendente'}
                        </span>
                    </div>
                </div>

                <div className="flex-1 p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start overflow-y-auto">
                    {/* Coluna Principal: Dados da Emenda */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-6 relative">
                            <h3 className="text-base font-semibold text-slate-800 mb-4 border-b pb-2">Dados da Emenda</h3>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-xs text-slate-500 uppercase block">Número</span>
                                        <span className="font-mono text-slate-700 font-medium">{officialCode}</span>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-500 uppercase block">Valor</span>
                                        <span className="font-mono text-slate-700 font-medium">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amendment.value || 0)}</span>
                                    </div>
                                </div>
                                <div>
                                    <span className="text-xs text-slate-500 uppercase block">Descrição Inicial</span>
                                    <p className="text-slate-700 text-sm">{amendment.description}</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                                    <div>
                                        <span className="text-xs text-slate-500 uppercase block">Classificação</span>
                                        <span className="text-slate-700">{amendment.classification || '—'}</span>
                                    </div>
                                    <div>
                                        <span className="text-xs text-slate-500 uppercase block">Categoria</span>
                                        <span className="text-slate-700">{amendment.category || institution?.category || 'Não definida'}</span>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <span className="text-xs text-slate-500 uppercase block mb-1">Objeto Detalhado</span>
                                    <div className="bg-white p-3 rounded border border-slate-200 text-sm text-slate-700 min-h-[60px] whitespace-pre-wrap">
                                        {amendment.objectDetail || 'Não informado'}
                                    </div>
                                </div>

                                {amendment.workPlan && (
                                    <div className="bg-white p-3 rounded border border-slate-200 space-y-3">
                                        <p className="font-semibold text-xs text-slate-500 uppercase">Plano de Trabalho</p>
                                        <div>
                                            <span className="text-xs text-slate-400 block">Nome do Projeto</span>
                                            <span className="text-sm text-slate-700">{amendment.workPlan.projectName || '—'}</span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 block">Finalidade</span>
                                            <span className="text-sm text-slate-700">{amendment.workPlan.purpose || '—'}</span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 block">Período</span>
                                            <span className="text-sm text-slate-700">{amendment.workPlan.period || '—'}</span>
                                        </div>
                                    </div>
                                )}

                                {amendment.attachments && amendment.attachments.length > 0 && (
                                    <div>
                                        <span className="text-xs text-slate-500 uppercase block mb-2">Anexos</span>
                                        <div className="flex flex-wrap gap-2">
                                            {amendment.attachments.map((att, idx) => (
                                                <a key={idx} href={att} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-slate-300 rounded text-sm hover:bg-slate-50">
                                                    <ExternalLink className="w-3 h-3" /> Anexo {idx + 1}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Aréa de Despacho */}
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                            <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                Área de Despacho
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="dispatch-note" className="text-purple-900">Motivo / Parecer do Gestor</Label>
                                    <Textarea
                                        id="dispatch-note"
                                        placeholder="Descreva o motivo da aprovação, necessidade de retificação ou reprovação..."
                                        className="mt-1 bg-white"
                                        value={dispatchNote}
                                        onChange={(e) => setDispatchNote(e.target.value)}
                                    />
                                </div>

                                <div className="flex flex-wrap gap-3 pt-2">
                                    <Button
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                                        onClick={() => handleDispatch('aprovada')}
                                        disabled={isProcessing}
                                    >
                                        <CheckCircle2 className="w-4 h-4 mr-2" />
                                        Aprovar
                                    </Button>
                                    <Button
                                        className="bg-amber-500 hover:bg-amber-600 text-white flex-1"
                                        onClick={() => handleDispatch('retificar')}
                                        disabled={isProcessing}
                                    >
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        Devolver p/ Retificar
                                    </Button>
                                    <Button
                                        className="bg-red-600 hover:bg-red-700 text-white flex-1"
                                        onClick={() => handleDispatch('reprovada')}
                                        disabled={isProcessing}
                                    >
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Reprovar
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Coluna Lateral */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <h3 className="text-sm font-semibold text-slate-800 mb-4">Linha do Tempo</h3>
                            {loadingHistory ? (
                                <div className="text-center py-4 text-slate-400">Carregando histórico...</div>
                            ) : (
                                <AmendmentTimeline
                                    history={history}
                                    createdAt={amendment.date}
                                    detailingSaved={amendment.detailingSaved}
                                    hasDetail={hasDetail}
                                    currentStatus={amendment.status}
                                    dispatchNote={amendment.dispatchNote}
                                />
                            )}
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Contatos da Instituição</h4>
                            <div className="space-y-2 text-sm">
                                <p><span className="text-slate-500">Email:</span> {inst?.contact?.email || '—'}</p>
                                <p><span className="text-slate-500">Fone:</span> {inst?.contact?.phone || '—'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
